/**
 * Responsive Utility Functions
 * Helper functions for responsive behavior
 */

/**
 * Get the current breakpoint based on window width
 * @returns {string} - One of: 'xs', 'sm', 'md', 'lg', 'xl', '2xl'
 */
export const getBreakpoint = () => {
  if (typeof window === 'undefined') return 'lg'; // SSR default

  const width = window.innerWidth;

  if (width >= 1536) return '2xl';
  if (width >= 1280) return 'xl';
  if (width >= 1024) return 'lg';
  if (width >= 768) return 'md';
  if (width >= 640) return 'sm';
  return 'xs';
};

/**
 * Check if the device supports touch
 * @returns {boolean} - True if touch is supported
 */
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Get the current viewport size
 * @returns {{width: number, height: number}} - Viewport dimensions
 */
export const getViewportSize = () => {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

/**
 * Check if viewport is mobile size (< 768px)
 * @returns {boolean} - True if mobile
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * Check if viewport is tablet size (768px - 1023px)
 * @returns {boolean} - True if tablet
 */
export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  const width = window.innerWidth;
  return width >= 768 && width < 1024;
};

/**
 * Check if viewport is desktop size (>= 1024px)
 * @returns {boolean} - True if desktop
 */
export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 1024;
};

/**
 * Get safe area insets (useful for iOS notch/home indicator)
 * @returns {{top: number, right: number, bottom: number, left: number}}
 */
export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined' || !CSS.supports) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  // Try to get CSS environment variables for safe area
  const getInset = (position) => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`env(safe-area-inset-${position})`)
      .trim();
    return value ? parseInt(value, 10) : 0;
  };

  return {
    top: getInset('top'),
    right: getInset('right'),
    bottom: getInset('bottom'),
    left: getInset('left'),
  };
};

/**
 * Debounce function for resize events
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait = 150) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for scroll events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit = 150) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Lock body scroll (useful for modals on mobile)
 */
export const lockScroll = () => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
};

/**
 * Unlock body scroll
 */
export const unlockScroll = () => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
};

/**
 * Check if element meets minimum touch target size (44x44px)
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} - True if meets minimum size
 */
export const meetsTouchTarget = (element) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.width >= 44 && rect.height >= 44;
};

